<template>
  <div class="admin-console-page board-page">
    <header class="console-page-head">
      <div class="console-page-copy">
        <p class="console-kicker">운영</p>
        <h2 class="console-title">게시판</h2>
      </div>
      <div v-if="screen === 'list'" class="console-head-side">
        <label class="board-search">
          <select v-model="searchField">
            <option value="title">제목</option>
            <option value="author">작성자</option>
          </select>
          <input v-model="keyword" type="text" placeholder="검색" @keydown.enter="search" />
        </label>
        <v-btn variant="flat" class="btn-register" @click="openCreate">
          <v-icon size="16" class="mr-1">mdi-plus</v-icon>
          글쓰기
        </v-btn>
      </div>
      <button v-if="screen === 'write'" type="button" class="board-back" @click="backList">목록으로</button>
    </header>

    <p v-if="errorText" class="console-alert">{{ errorText }}</p>

    <article v-if="screen === 'read' && reading" class="board-article">
      <header class="board-article-head">
        <span v-if="reading.kind === 'N'" class="board-flag is-notice">공지사항</span>
        <span v-if="reading.pinned" class="notice-pin">고정</span>
        <span v-if="isNewNotice(reading)" class="board-new">신규</span>
        <span v-if="reading.kind === 'P'" class="board-flag is-update">업데이트</span>
        <h3 class="board-article-title">{{ reading.title }}</h3>
        <p class="board-article-meta">
          {{ reading.authorName || '관리자' }}
          <span>{{ formatDateTime(reading.createdAt) }}</span>
          <span>조회 {{ reading.hit }}</span>
          <span v-if="reading.kind === 'Q'">비공개</span>
          <span v-if="reading.kind === 'Q'">{{ reading.answerStatus === 'A' ? '답변 완료' : '답변 대기' }}</span>
        </p>
        <div class="board-article-actions">
          <button v-if="canNotice && reading.kind === 'N'" type="button" class="board-text-btn" @click="togglePin">
            {{ reading.pinned ? '고정 해제' : '상단 고정' }}
          </button>
          <button v-if="canEdit" type="button" class="board-text-btn" @click="openEdit">수정</button>
          <button v-if="canRemove" type="button" class="board-text-btn is-danger" @click="askRemove">삭제</button>
        </div>
      </header>
      <p v-if="reading.locked" class="board-locked">비밀글입니다.</p>
      <BoardBody v-else class="board-article-body" :body="reading.body" />

      <section v-if="reading.kind === 'Q' && !reading.locked" class="board-reply">
        <h4>답변</h4>
        <template v-if="reading.reply">
          <p class="board-reply-meta">{{ reading.replyName || '관리자' }} · {{ formatDateTime(reading.replyAt) }}</p>
          <BoardBody :body="reading.reply" />
        </template>
        <p v-else-if="!canAnswer" class="board-reply-empty">아직 답변이 없습니다.</p>
        <template v-if="canAnswer">
          <PasteImageField
            :key="replyKey"
            v-model="replyText"
            placeholder="답변을 입력하세요. 사진은 붙여넣기 하면 들어갑니다."
          />
          <p v-if="replyError" class="console-alert">{{ replyError }}</p>
          <div class="board-compose-actions">
            <v-btn variant="flat" class="btn-save" :loading="replying" @click="saveReply">답변 저장</v-btn>
          </div>
        </template>
      </section>

      <section v-if="!reading.locked" class="board-comments">
        <h4>댓글 {{ reading.comments.length }}</h4>
        <ul v-if="reading.comments.length" class="board-comment-list">
          <li v-for="comment in reading.comments" :key="comment.idx">
            <div class="board-comment-meta">
              <span>{{ comment.authorName || '매니저' }}</span>
              <span>{{ formatDateTime(comment.createdAt) }}</span>
              <button v-if="canTouchComment(comment) && editingCommentId !== comment.idx" type="button" class="board-text-btn" @click="startEditComment(comment)">수정</button>
              <button v-if="canTouchComment(comment)" type="button" class="board-text-btn is-danger" @click="removeComment(comment)">삭제</button>
            </div>
            <template v-if="editingCommentId === comment.idx">
              <PasteImageField :key="editCommentKey" v-model="editCommentText" />
              <div class="board-compose-actions">
                <v-btn variant="text" class="btn-cancel" :disabled="commentSaving" @click="cancelEditComment">취소</v-btn>
                <v-btn variant="flat" class="btn-save" :loading="commentSaving" @click="saveEditComment">저장</v-btn>
              </div>
            </template>
            <BoardBody v-else :body="comment.body" />
          </li>
        </ul>
        <p v-else class="board-reply-empty">댓글이 없습니다.</p>
        <PasteImageField :key="commentKey" v-model="commentText" placeholder="댓글을 입력하세요. 사진은 붙여넣기 하면 들어갑니다." />
        <p v-if="commentError" class="console-alert">{{ commentError }}</p>
        <div class="board-compose-actions">
          <v-btn variant="flat" class="btn-save" :loading="commenting" @click="saveComment">댓글 등록</v-btn>
        </div>
      </section>

      <div class="board-near">
        <button type="button" :disabled="!reading.newer" @click="openNear(reading.newer)">
          <span>다음글</span>
          <em>{{ reading.newer?.title || '다음글이 없습니다' }}</em>
        </button>
        <button type="button" :disabled="!reading.older" @click="openNear(reading.older)">
          <span>이전글</span>
          <em>{{ reading.older?.title || '이전글이 없습니다' }}</em>
        </button>
      </div>
      <div class="board-article-foot">
        <button type="button" class="board-foot-btn" @click="backList">목록</button>
        <button type="button" class="board-foot-btn is-primary" @click="openCreate">글쓰기</button>
      </div>
    </article>

    <nav v-if="screen === 'list'" class="board-tabs">
      <button type="button" :class="{ 'is-on': tab === 'A' }" @click="setTab('A')">전체</button>
      <button type="button" :class="{ 'is-on': tab === 'N' }" @click="setTab('N')">공지사항</button>
      <button type="button" :class="{ 'is-on': tab === 'Q' }" @click="setTab('Q')">문의요청</button>
      <button type="button" :class="{ 'is-on': tab === 'P' }" @click="setTab('P')">업데이트</button>
    </nav>

    <section v-if="screen === 'list'" class="board-sheet">
      <div v-if="loading" class="console-empty">불러오는 중</div>
      <div v-else-if="!pinned.length && !posts.length" class="console-empty">등록된 글이 없습니다.</div>
      <table v-else class="board-table">
        <thead>
          <tr>
            <th class="col-no">번호</th>
            <th>제목</th>
            <th class="col-who">작성자</th>
            <th class="col-date">등록일</th>
            <th class="col-hit">조회</th>
            <th class="col-cmt">댓글</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in pinned"
            :key="`pin-${item.idx}`"
            class="is-notice"
            :class="{ 'is-current': screen === 'read' && reading?.idx === item.idx }"
            @click="openRead(item)"
          >
            <td class="col-no">공지</td>
            <td>
              <div class="board-title-line">
                <span class="notice-pin">고정</span>
                <span v-if="item.status === 'N'" class="board-flag is-hide">숨김</span>
                <span v-if="isNewNotice(item)" class="board-new">신규</span>
                <span class="board-title">{{ item.title }}</span>
              </div>
            </td>
            <td class="col-who">{{ item.authorName || '—' }}</td>
            <td class="col-date">{{ formatDate(item.createdAt) }}</td>
            <td class="col-hit">{{ item.hit }}</td>
            <td class="col-cmt">{{ item.commentCount }}</td>
          </tr>
          <tr
            v-for="(item, index) in posts"
            :key="`p-${item.idx}`"
            :class="{ 'is-current': screen === 'read' && reading?.idx === item.idx, 'is-notice': item.kind === 'N' && item.pinned }"
            @click="openRead(item)"
          >
            <td class="col-no">{{ item.kind === 'N' && item.pinned ? '공지' : rowNo(index) }}</td>
            <td>
              <div class="board-title-line">
                <span v-if="item.kind === 'N' && item.pinned" class="notice-pin">고정</span>
                <span v-if="tab === 'A' && item.kind === 'N'" class="board-flag is-notice">공지</span>
                <span v-if="tab === 'A' && item.kind === 'Q'" class="board-flag is-ask">문의</span>
                <span v-if="tab === 'A' && item.kind === 'P'" class="board-flag is-update">업데이트</span>
                <span v-if="item.status === 'N'" class="board-flag is-hide">숨김</span>
                <span v-if="isNewNotice(item)" class="board-new">신규</span>
                <span v-if="item.kind === 'Q' && item.answerStatus === 'A'" class="board-flag is-done">답변달림</span>
                <v-icon v-if="item.kind === 'Q'" size="14" class="board-lock">mdi-lock</v-icon>
                <span class="board-title">{{ item.title }}</span>
              </div>
            </td>
            <td class="col-who">{{ item.authorName || '—' }}</td>
            <td class="col-date">{{ formatDate(item.createdAt) }}</td>
            <td class="col-hit">{{ item.hit }}</td>
            <td class="col-cmt">{{ item.commentCount }}</td>
          </tr>
        </tbody>
      </table>
      <TablePagination
        v-if="!loading && (totalCount || pinned.length)"
        :total-count="totalCount + pinned.length"
        :current-page="page"
        :items-per-page="limit"
        :total-pages="totalPages"
        :page-size-options="[15, 30]"
        @update:current-page="onPage"
        @update:items-per-page="onLimit"
      />
    </section>

    <section v-if="screen === 'write'" class="board-compose">
      <h3 class="board-compose-title">{{ editingId ? '글 수정' : '글쓰기' }}</h3>
      <div v-if="!editingId" class="kind-picks">
        <button v-if="canNotice" type="button" :class="{ 'is-on': form.kind === 'N' }" @click="form.kind = 'N'">공지사항</button>
        <button type="button" :class="{ 'is-on': form.kind === 'Q' }" @click="form.kind = 'Q'">문의요청</button>
        <button v-if="canNotice" type="button" :class="{ 'is-on': form.kind === 'P' }" @click="form.kind = 'P'">업데이트</button>
      </div>
      <label class="console-field">
        <span>제목</span>
        <input v-model="form.title" type="text" maxlength="120" placeholder="제목을 입력하세요" />
      </label>
      <div class="console-field">
        <span>내용</span>
        <PasteImageField v-if="screen === 'write'" :key="editorKey" v-model="form.body" />
      </div>
      <p v-if="form.kind === 'Q'" class="board-private-note">문의는 작성한 본인과 관리자만 볼 수 있습니다.</p>
      <div v-if="form.kind === 'N' || form.kind === 'P'" class="notice-form-options">
        <label v-if="form.kind === 'N'" class="console-check">
          <input v-model="form.pinned" type="checkbox" />
          <span>상단 고정 · 목록 맨 위에 둡니다</span>
        </label>
        <label class="console-field console-field--inline">
          <span>상태</span>
          <select v-model="form.status">
            <option value="Y">게시</option>
            <option value="N">숨김</option>
          </select>
        </label>
      </div>
      <p v-if="formError" class="console-alert">{{ formError }}</p>
      <div class="board-compose-actions">
        <v-btn variant="text" class="btn-cancel" :disabled="saving" @click="backList">취소</v-btn>
        <v-btn variant="flat" class="btn-save" :loading="saving" @click="save">{{ editingId ? '저장' : '등록' }}</v-btn>
      </div>
    </section>

  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { boardApi } from '@/services/api';
import BoardBody from '@/components/admin/BoardBody.vue';
import PasteImageField from '@/components/admin/PasteImageField.vue';
import TablePagination from '@/components/common/TablePagination.vue';

type Kind = 'N' | 'Q' | 'P';
type Tab = 'A' | Kind;

interface BoardItem {
  idx: number;
  kind: Kind;
  title: string;
  body: string;
  secret: boolean;
  pinned: boolean;
  status: 'Y' | 'N';
  answerStatus: '-' | 'W' | 'A';
  authorName: string;
  reply: string;
  replyName: string;
  replyAt: string | null;
  createdAt: string | null;
  hit: number;
  commentCount: number;
  mine: boolean;
  locked: boolean;
  preview: string;
  newer: NearPost | null;
  older: NearPost | null;
  comments: BoardComment[];
}

interface NearPost {
  idx: number;
  title: string;
  createdAt: string | null;
}

interface BoardComment {
  idx: number;
  body: string;
  authorName: string;
  createdAt: string | null;
  mine: boolean;
}

const store = useStore();
const route = useRoute();
const router = useRouter();
const level = computed(() => Number(store.getters.actualRoleLevel || store.getters.roleLevel || 0));
const canNotice = computed(() => level.value >= 80);
const canAnswer = computed(() => level.value >= 80);

const posts = ref<BoardItem[]>([]);
const pinned = ref<BoardItem[]>([]);
const tab = ref<Tab>('A');
const keyword = ref('');
const searchField = ref<'title' | 'author'>('title');
const page = ref(1);
const limit = ref(15);
const totalCount = ref(0);
const loading = ref(false);
const saving = ref(false);
const replying = ref(false);
const errorText = ref('');
const formError = ref('');
const replyError = ref('');
const screen = ref<'list' | 'read' | 'write'>('list');
const reading = ref<BoardItem | null>(null);
const editingId = ref<number | null>(null);
const editorKey = ref(0);
const replyKey = ref(0);
const replyText = ref('');
const commentText = ref('');
const commentError = ref('');
const commenting = ref(false);
const commentKey = ref(0);
const editingCommentId = ref<number | null>(null);
const editCommentText = ref('');
const editCommentKey = ref(0);
const commentSaving = ref(false);
const form = ref({
  kind: 'Q' as Kind,
  title: '',
  body: '',
  secret: false,
  pinned: false,
  status: 'Y' as 'Y' | 'N',
});

const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / limit.value)));

const rowNo = (index: number) => totalCount.value - (page.value - 1) * limit.value - index;

const NEW_NOTICE_MS = 7 * 24 * 60 * 60 * 1000;

const isNewNotice = (item: { kind?: string; createdAt: string | null }) => {
  if (item.kind !== 'N' || !item.createdAt) return false;
  const posted = new Date(String(item.createdAt).replace(' ', 'T'));
  if (Number.isNaN(posted.getTime())) return false;
  const age = Date.now() - posted.getTime();
  return age >= 0 && age < NEW_NOTICE_MS;
};

const formatDate = (value: string | null) => (value ? String(value).substring(0, 10) : '');
const formatDateTime = (value: string | null) => (value ? String(value).substring(0, 16).replace('T', ' ') : '');

const canEdit = computed(() => {
  const item = reading.value;
  if (!item || item.locked) return false;
  if (item.kind === 'N' || item.kind === 'P') return canNotice.value;
  return item.mine && item.answerStatus !== 'A';
});

const canRemove = computed(() => {
  const item = reading.value;
  if (!item) return false;
  if (item.kind === 'N' || item.kind === 'P') return canNotice.value;
  if (canAnswer.value) return true;
  return item.mine && item.answerStatus !== 'A';
});

const load = async () => {
  loading.value = true;
  errorText.value = '';
  try {
    const res = await boardApi.list({
      page: page.value,
      limit: limit.value,
      keyword: keyword.value.trim() || undefined,
      field: searchField.value,
      kind: tab.value,
    });
    pinned.value = res.data?.data?.pinned || [];
    posts.value = res.data?.data?.posts || [];
    totalCount.value = Number(res.data?.data?.pagination?.total) || 0;
  } catch (error) {
    console.error('게시판 목록 실패:', error);
    errorText.value = '게시판을 불러오지 못했습니다.';
  } finally {
    loading.value = false;
  }
};

const setTab = (next: Tab) => {
  if (tab.value === next) return;
  tab.value = next;
  page.value = 1;
  load();
};

const search = () => {
  page.value = 1;
  load();
};

const onPage = (next: number) => {
  page.value = next;
  load();
};

const onLimit = (next: number) => {
  limit.value = next;
  page.value = 1;
  load();
};

const readPost = async (idx: number, peek = false) => {
  const res = await boardApi.detail(idx, {
    keyword: keyword.value.trim() || undefined,
    field: searchField.value,
    nav: tab.value,
    ...(peek ? { peek: 1 } : {}),
  });
  reading.value = res.data?.data || null;
  replyText.value = reading.value?.reply || '';
};

const postIdFromRoute = () => {
  const raw = route.query.post;
  const value = Array.isArray(raw) ? raw[0] : raw;
  const idx = Number(value);
  return Number.isFinite(idx) && idx > 0 ? idx : 0;
};

let readToken = 0;

const showPost = async (idx: number) => {
  const token = ++readToken;
  try {
    await readPost(idx);
    if (token !== readToken || postIdFromRoute() !== idx) return;
    replyError.value = '';
    commentError.value = '';
    commentText.value = '';
    commentKey.value += 1;
    replyKey.value += 1;
    screen.value = 'read';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } catch (error) {
    if (token !== readToken) return;
    errorText.value = '글을 불러오지 못했습니다.';
  }
};

const openRead = (item: { idx: number }, replace = false) => {
  if (postIdFromRoute() === item.idx && screen.value === 'read') return;
  const query = { ...route.query, post: String(item.idx) };
  if (replace) router.replace({ name: 'board', query });
  else router.push({ name: 'board', query });
};

const openNear = (item: NearPost | null) => {
  if (!item) return;
  openRead(item, true);
};

const listQuery = () => {
  const query = { ...route.query };
  delete query.post;
  return query;
};

const cameFromList = () => {
  const back = window.history.state?.back;
  return typeof back === 'string' && back.split('?')[0] === '/board' && !/[?&]post=/.test(back);
};

const openCreate = () => {
  editingId.value = null;
  const nextKind: Kind = (tab.value === 'N' || tab.value === 'P') && canNotice.value ? tab.value : 'Q';
  form.value = {
    kind: nextKind,
    title: '',
    body: '',
    secret: false,
    pinned: false,
    status: 'Y',
  };
  formError.value = '';
  editorKey.value += 1;
  screen.value = 'write';
};

const backList = () => {
  if (!postIdFromRoute()) {
    screen.value = 'list';
    return;
  }
  if (cameFromList()) {
    router.back();
    return;
  }
  router.replace({ name: 'board', query: listQuery() });
};

const openEdit = () => {
  if (!reading.value) return;
  editingId.value = reading.value.idx;
  form.value = {
    kind: reading.value.kind,
    title: reading.value.title,
    body: reading.value.body,
    secret: reading.value.secret,
    pinned: reading.value.pinned,
    status: reading.value.status === 'N' ? 'N' : 'Y',
  };
  formError.value = '';
  editorKey.value += 1;
  screen.value = 'write';
};

const save = async () => {
  formError.value = '';
  const title = form.value.title.trim();
  const body = form.value.body.trim();
  if (!title) {
    formError.value = '제목을 입력해 주세요.';
    return;
  }
  if (!body) {
    formError.value = '내용을 입력해 주세요.';
    return;
  }
  if (editingId.value && !window.confirm('수정한 내용을 저장할까요?')) return;
  saving.value = true;
  try {
    if (editingId.value) {
      await boardApi.update(editingId.value, {
        title,
        body,
        secret: form.value.kind === 'Q',
        pinned: form.value.pinned,
        status: form.value.status,
      });
    } else {
      await boardApi.create({
        kind: form.value.kind,
        title,
        body,
        secret: form.value.kind === 'Q',
        pinned: form.value.pinned,
        status: form.value.status,
      });
    }
    tab.value = form.value.kind;
    page.value = 1;
    if (postIdFromRoute()) {
      if (cameFromList()) router.back();
      else router.replace({ name: 'board', query: listQuery() });
    } else {
      screen.value = 'list';
    }
    await load();
  } catch (error: any) {
    formError.value = error?.response?.data?.message || '저장에 실패했습니다.';
  } finally {
    saving.value = false;
  }
};

const saveReply = async () => {
  if (!reading.value) return;
  replyError.value = '';
  if (!replyText.value.trim()) {
    replyError.value = '답변 내용을 입력해 주세요.';
    return;
  }
  const replyAsk = reading.value.reply ? '답변을 수정할까요?' : '답변을 등록할까요?';
  if (!window.confirm(replyAsk)) return;
  replying.value = true;
  try {
    await boardApi.reply(reading.value.idx, replyText.value.trim());
    await readPost(reading.value.idx, true);
    await load();
  } catch (error: any) {
    replyError.value = error?.response?.data?.message || '답변 저장에 실패했습니다.';
  } finally {
    replying.value = false;
  }
};

const saveComment = async () => {
  if (!reading.value) return;
  commentError.value = '';
  if (!commentText.value.trim()) {
    commentError.value = '댓글을 입력해 주세요.';
    return;
  }
  commenting.value = true;
  try {
    await boardApi.comment(reading.value.idx, commentText.value.trim());
    commentText.value = '';
    commentKey.value += 1;
    await readPost(reading.value.idx, true);
    await load();
  } catch (error: any) {
    commentError.value = error?.response?.data?.message || '댓글을 등록하지 못했습니다.';
  } finally {
    commenting.value = false;
  }
};

const canTouchComment = (comment: BoardComment) => comment.mine || canAnswer.value;

const startEditComment = (comment: BoardComment) => {
  editingCommentId.value = comment.idx;
  editCommentText.value = comment.body;
  editCommentKey.value += 1;
  commentError.value = '';
};

const cancelEditComment = () => {
  editingCommentId.value = null;
  editCommentText.value = '';
};

const saveEditComment = async () => {
  if (!reading.value || !editingCommentId.value) return;
  commentError.value = '';
  if (!editCommentText.value.trim()) {
    commentError.value = '댓글을 입력해 주세요.';
    return;
  }
  if (!window.confirm('댓글 수정을 저장할까요?')) return;
  commentSaving.value = true;
  try {
    await boardApi.updateComment(reading.value.idx, editingCommentId.value, editCommentText.value.trim());
    cancelEditComment();
    await readPost(reading.value.idx, true);
    await load();
  } catch (error: any) {
    commentError.value = error?.response?.data?.message || '댓글을 수정하지 못했습니다.';
  } finally {
    commentSaving.value = false;
  }
};

const removeComment = async (comment: BoardComment) => {
  if (!reading.value) return;
  if (!window.confirm('이 댓글을 삭제할까요?')) return;
  try {
    await boardApi.removeComment(reading.value.idx, comment.idx);
    if (editingCommentId.value === comment.idx) cancelEditComment();
    await readPost(reading.value.idx, true);
    await load();
  } catch (error) {
    commentError.value = '댓글을 삭제하지 못했습니다.';
  }
};

const togglePin = async () => {
  const item = reading.value;
  if (!item || item.kind !== 'N') return;
  const next = !item.pinned;
  const ask = next ? '이 공지를 목록 상단에 고정할까요?' : '상단 고정을 해제할까요?';
  if (!window.confirm(ask)) return;
  try {
    await boardApi.update(item.idx, {
      title: item.title,
      body: item.body,
      secret: item.secret,
      pinned: next,
      status: item.status,
    });
    await readPost(item.idx, true);
    await load();
  } catch (error) {
    errorText.value = '고정을 바꾸지 못했습니다.';
  }
};

const askRemove = async () => {
  if (!reading.value) return;
  if (!window.confirm('이 글을 삭제할까요?')) return;
  try {
    await boardApi.remove(reading.value.idx);
    reading.value = null;
    if (postIdFromRoute()) {
      if (cameFromList()) router.back();
      else router.replace({ name: 'board', query: listQuery() });
    } else {
      screen.value = 'list';
    }
    await load();
  } catch (error) {
    errorText.value = '삭제에 실패했습니다.';
  }
};

watch(() => route.query.post, () => {
  const idx = postIdFromRoute();
  if (!idx) {
    readToken += 1;
    if (screen.value !== 'list') screen.value = 'list';
    return;
  }
  if (screen.value === 'read' && reading.value?.idx === idx) return;
  showPost(idx);
});

onMounted(async () => {
  await load();
  const idx = postIdFromRoute();
  if (idx) showPost(idx);
});
</script>
